import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { EXTENSION_QUERY_KEYS } from "./useExtensions";

export interface CreatePuckExtensionRequest {
    name: string;
    extensionPoint: string;
    priority: number;
    status: "active" | "inactive" | "draft";
    puckData?: string;
    conditions?: Record<string, unknown>;
    allowedRoles?: string[];
    sectors?: string[];
}

export interface UpdatePuckExtensionRequest {
    id: number;
    name?: string;
    extensionPoint?: string;
    priority?: number;
    status?: "active" | "inactive" | "draft";
    puckData?: string;
    conditions?: Record<string, unknown>;
    allowedRoles?: string[];
    sectors?: string[];
}

export const useCreatePuckExtension = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreatePuckExtensionRequest) => {
            const response = await loggedInApiCall.post("/api/puck_extensions", {
                name: data.name,
                extensionPoint: data.extensionPoint,
                priority: data.priority,
                status: data.status,
                puckData: data.puckData ?? JSON.stringify({ content: [], root: {} }),
                conditions: data.conditions || {},
                allowedRoles: data.allowedRoles ?? [],
                sectors: data.sectors ?? [],
            });
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all extension queries to refresh the data
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.EXTENSIONS]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.BY_POINT]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.BY_PAGE]);
        },
    });
};

export const useUpdatePuckExtension = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: UpdatePuckExtensionRequest) => {
            const { id, ...updateData } = data;
            const response = await loggedInApiCall.patch(`/api/puck_extensions/${id}`, updateData, {
                headers: {
                    "Content-Type": "application/merge-patch+json",
                },
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.EXTENSIONS]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.EXTENSION]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.BY_POINT]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.BY_PAGE]);
        },
    });
};

export const useDeletePuckExtension = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (extensionId: number) => {
            const response = await loggedInApiCall.delete(`/api/puck_extensions/${extensionId}`);
            return response.data;
        },
        onSuccess: (_, extensionId) => {
            queryClient.removeQueries([EXTENSION_QUERY_KEYS.EXTENSION, extensionId], { exact: true });
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.EXTENSIONS]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.BY_POINT]);
            queryClient.invalidateQueries([EXTENSION_QUERY_KEYS.BY_PAGE]);
        },
    });
};
